import { Tooltip } from '@civilgrid/ui/components';
import { IconButton } from '@civilgrid/ui/index';
import { useEffect, useState } from 'react';
import { useSnapshot } from 'valtio';
import { EditorMode } from '~/common/types';
import Button from '~/components2/forms/Button';
import Popover from '~/components2/Popover/Popover';
import type { BasicMapStore } from '../..';
import { clearMeasurement } from './common';
import MeasurementDisplay from './MeasurementDisplay';
import MeasureSurface from './MeasureSurface';
import { areaStore, lengthStore } from './store';

export default function Measure({ store }: { store: BasicMapStore }) {
  const [isMeasuring, setIsMeasuring] = useState(false);
  const { editorMode } = useSnapshot(store) as typeof store;

  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy
  useEffect(() => {
    return () => {
      resetStores();
    };
  }, []);

  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy
  useEffect(() => {
    if (editorMode === EditorMode.DrawMeasureDistance || editorMode === EditorMode.DrawMeasureArea) {
      setIsMeasuring(true);
      resetStores();
    } else {
      setIsMeasuring(false);
    }
  }, [editorMode]);

  const resetStores = () => {
    lengthStore.length = 0;
    areaStore.length = 0;
    areaStore.area = 0;
  };

  const discardLiveMeasurement = () => {
    if (isMeasuring) {
      setIsMeasuring(false);
      clearMeasurement(store);
    }
  };

  return (
    <div className="z-10">
      <Popover
        side="right"
        sideOffset={10}
        align="end"
        onOpenChange={discardLiveMeasurement}
        trigger={(isSelectingMeasureTool) => (
          <Tooltip asChild text={isSelectingMeasureTool ? 'Close' : 'Measure'} side="right" sideOffset={10}>
            <IconButton
              variant="secondary"
              icon={isSelectingMeasureTool ? 'chevronLeft' : 'rulerHorizontal'}
              size="icon"
              iconSize={14}
              className="w-7 h-7"
              type="button"
              aria-label={isSelectingMeasureTool ? 'Close measure tool' : 'Measure'}
              data-testid="map-measure-trigger"
            />
          </Tooltip>
        )}
      >
        {(closePopover) => (
          <div className="flex flex-col gap-1.5">
            <Button
              alternate
              onClick={() => {
                store.setEditorMode(EditorMode.DrawMeasureDistance);
                closePopover();
              }}
              data-testid="map-measure-length"
            >
              Length
            </Button>
            <Button
              alternate
              onClick={() => {
                store.setEditorMode(EditorMode.DrawMeasureArea);
                closePopover();
              }}
              data-testid="map-measure-area"
            >
              Area
            </Button>
          </div>
        )}
      </Popover>

      {isMeasuring && (
        <>
          <MeasureSurface store={store} />
          <MeasurementDisplay store={store} />
        </>
      )}
    </div>
  );
}
